import React, { memo, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import PrivacyPolicy from '../pages/PrivacyPolicy';

// Memoized Dashboard component to prevent unnecessary re-renders
const MemoizedDashboard = memo(Dashboard);

// Custom hook for permission-based routing logic with localStorage caching
function usePermissionRouting() {
    // Use cached permissions to avoid repeated API calls
    const permissionData = useCachedPermissions();

    // Memoize the return object to prevent unnecessary re-renders
    return useMemo(() => ({
        hasShowInsight: permissionData.hasShowInsight,
        hasShowProject: permissionData.hasShowProject,
        hasAddProject: permissionData.hasAddProject,
        hasUpdateProject: permissionData.hasUpdateProject,
        hasDeleteProject: permissionData.hasDeleteProject,
        hasShowUnit: permissionData.hasShowUnit,
        hasShowOffers: permissionData.hasShowOffers,
        hasShowTicket: permissionData.hasShowTicket,
        permissionsLoading: permissionData.permissionsLoading,
        permissionsError: permissionData.permissionsError,
        isReady: permissionData.isReady,
        fromCache: permissionData.fromCache
    }), [
        permissionData.hasShowInsight,
        permissionData.hasShowProject,
        permissionData.hasAddProject,
        permissionData.hasUpdateProject,
        permissionData.hasDeleteProject,
        permissionData.hasShowUnit,
        permissionData.hasShowOffers,
        permissionData.hasShowTicket,
        permissionData.permissionsLoading,
        permissionData.permissionsError,
        permissionData.isReady,
        permissionData.fromCache
    ]);
}

// Centralized loading component for better UX consistency
const LoadingScreen = memo(function LoadingScreen({ message = "Loading..." }) {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#1a1a1a',
            color: '#ffffff',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #333',
                borderTop: '3px solid #FABD08',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
            }} />
            <div style={{ fontSize: '16px', fontWeight: '500' }}>{message}</div>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
});

function PrivateRoute({ children }) {
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const location = useLocation();

    // Check for tokens in localStorage as fallback
    const hasTokens = localStorage.getItem('access_token') && localStorage.getItem('refresh_token');

    // If not authenticated and no tokens in localStorage, redirect to login
    if (!isAuthenticated && !hasTokens) {
        // Clear any leftover tokens when redirecting to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return <Navigate to="/login" replace />;
    }

    // If we have tokens but not authenticated yet, show loading
    if (!isAuthenticated && hasTokens) {
        return <LoadingScreen message="Authenticating..." />;
    }

    return children;
}

function DashboardRedirect() {
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const { hasShowInsight, hasShowProject, permissionsLoading, permissionsError, isReady, fromCache } = usePermissionRouting();

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Handle permission loading errors
    if (permissionsError) {
        console.error('Permissions loading error:', permissionsError);
        // Default to protocols (offers) if permissions fail to load
        return <Navigate to="/protocols" replace />;
    }

    // Wait for permissions to be ready before making redirect decision
    // This is crucial to ensure we don't redirect to projects before permissions load
    if (!isReady) {
        return <LoadingScreen message="Loading permissions..." />;
    }

    // Redirect based on permissions - prioritize overview, then projects, then protocols
    let defaultPath;
    if (hasShowInsight) {
        defaultPath = '/overview';
    } else if (hasShowProject) {
        defaultPath = '/projects';
    } else {
        defaultPath = '/protocols'; // Fallback to offers/protocols tab
    }

    console.log('🔀 DashboardRedirect - hasShowInsight:', hasShowInsight, 'hasShowProject:', hasShowProject, '-> redirecting to:', defaultPath);
    return <Navigate to={defaultPath} replace />;
}

const ProjectsRouteHandler = memo(function ProjectsRouteHandler() {
    const { hasShowInsight, hasShowProject, hasShowUnit, permissionsLoading, permissionsError, isReady, fromCache } = usePermissionRouting();

    // Handle permission loading errors - redirect to protocols as fallback
    if (permissionsError) {
        console.error('Permissions loading error in projects route:', permissionsError);
        return <Navigate to="/protocols" replace />;
    }

    // Only show loading if permissions are not cached and still loading
    if (permissionsLoading && !fromCache) {
        return <LoadingScreen message="Checking permissions..." />;
    }

    // If user doesn't have show_project permission, redirect to protocols
    if (isReady && !hasShowProject) {
        return <Navigate to="/protocols" replace />;
    }

    // Allow access to projects tab if user has permission
    return <MemoizedDashboard />;
});

const ProtocolsRouteHandler = memo(function ProtocolsRouteHandler() {
    const { hasShowOffers, hasShowProject, permissionsLoading, permissionsError, isReady, fromCache } = usePermissionRouting();

    // Handle permission loading errors - redirect to fallback tab
    if (permissionsError) {
        console.error('Permissions loading error in protocols route:', permissionsError);
        // Redirect to projects if available, otherwise customers (if that has permission)
        const fallbackPath = hasShowProject ? '/projects' : '/customers';
        return <Navigate to={fallbackPath} replace />;
    }

    // Only show loading if permissions are not cached and still loading
    if (permissionsLoading && !fromCache) {
        return <LoadingScreen message="Verifying access..." />;
    }

    // If user doesn't have show_offers permission, redirect to projects
    if (isReady && !hasShowOffers) {
        return <Navigate to="/projects" replace />;
    }

    // Allow access to protocols tab if user has permission
    return <MemoizedDashboard />;
});

const TicketsRouteHandler = memo(function TicketsRouteHandler() {
    const { hasShowTicket, hasShowProject, hasShowOffers, permissionsLoading, permissionsError, isReady, fromCache } = usePermissionRouting();

    // Handle permission loading errors - redirect to fallback tab
    if (permissionsError) {
        console.error('Permissions loading error in tickets route:', permissionsError);
        // Redirect to projects if available, otherwise protocols if available, otherwise customers
        const fallbackPath = hasShowProject ? '/projects' : hasShowOffers ? '/protocols' : '/customers';
        return <Navigate to={fallbackPath} replace />;
    }

    // Only show loading if permissions are not cached and still loading
    if (permissionsLoading && !fromCache) {
        return <LoadingScreen message="Verifying access..." />;
    }

    // If user doesn't have show_ticket permission, redirect to appropriate fallback
    if (isReady && !hasShowTicket) {
        const fallbackPath = hasShowProject ? '/projects' : hasShowOffers ? '/protocols' : '/customers';
        return <Navigate to={fallbackPath} replace />;
    }

    // Allow access to tickets tab if user has permission
    return <MemoizedDashboard />;
});

const OverviewRouteHandler = memo(function OverviewRouteHandler() {
    const { hasShowInsight, hasShowProject, hasShowOffers, hasShowTicket, permissionsLoading, permissionsError, isReady, fromCache } = usePermissionRouting();

    // Handle permission loading errors - redirect to fallback tab
    if (permissionsError) {
        console.error('Permissions loading error in overview route:', permissionsError);
        // Redirect to projects if available, otherwise protocols
        const fallbackPath = hasShowProject ? '/projects' : hasShowOffers ? '/protocols' : hasShowTicket ? '/tickets' : '/customers';
        return <Navigate to={fallbackPath} replace />;
    }

    // Only show loading if permissions are not cached and still loading
    if (permissionsLoading && !fromCache) {
        return <LoadingScreen message="Verifying access..." />;
    }

    // If user doesn't have show_insight permission, redirect to appropriate fallback
    if (isReady && !hasShowInsight) {
        const fallbackPath = hasShowProject ? '/projects' : hasShowOffers ? '/protocols' : hasShowTicket ? '/tickets' : '/customers';
        return <Navigate to={fallbackPath} replace />;
    }

    // Otherwise, show the dashboard normally
    return <MemoizedDashboard />;
});



export default function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

            {/* Direct Tab Routes - These need to come first */}
            <Route path="/overview" element={
                <PrivateRoute>
                    <OverviewRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/projects" element={
                <PrivateRoute>
                    <ProjectsRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/projects/project/:projectId" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/projects/project/:projectId/units" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/projects/unit/:unitId" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/protocols" element={
                <PrivateRoute>
                    <ProtocolsRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/customers" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/customers/groups" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/groups" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/myclub" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/updates" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/settings" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            {/* Finance Routes */}
            <Route path="/finance" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/finance/paid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/finance/unpaid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            {/* Maintenance Routes */}
            <Route path="/maintenance" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/maintenance/paid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/maintenance/unpaid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            {/* Utility Routes */}
            <Route path="/utility" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/utility/paid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/utility/unpaid" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            {/* Tickets Routes */}
            <Route path="/tickets" element={
                <PrivateRoute>
                    <TicketsRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/tickets/requests" element={
                <PrivateRoute>
                    <TicketsRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/tickets/closed" element={
                <PrivateRoute>
                    <TicketsRouteHandler />
                </PrivateRoute>
            } />
            <Route path="/tickets/categories" element={
                <PrivateRoute>
                    <TicketsRouteHandler />
                </PrivateRoute>
            } />

            {/* Logs Routes */}
            <Route path="/logs" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/logs/admin" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />
            <Route path="/logs/user" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            {/* Deprecated Dashboard Routes - Redirect based on permissions */}
            <Route path="/dashboard" element={
                <PrivateRoute>
                    <DashboardRedirect />
                </PrivateRoute>
            } />
            <Route path="/dashboard/:tab" element={
                <PrivateRoute>
                    <DashboardRedirect />
                </PrivateRoute>
            } />
            <Route path="/dashboard/:tab/:subtab" element={
                <PrivateRoute>
                    <DashboardRedirect />
                </PrivateRoute>
            } />

            {/* Special Routes */}
            <Route path="/add-customer/:unitId" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            <Route path="/unit/:unitId/update-installments" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            <Route path="/add-unit/:customerId" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            <Route path="/customer/:customerId" element={
                <PrivateRoute>
                    <MemoizedDashboard />
                </PrivateRoute>
            } />

            <Route path="/" element={<DashboardRedirect />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
    );
} 